import { CommentPlugin } from '../lib/comment-plugin';
import { Annotation } from './annotation';

export class AnnotationState extends CommentPlugin {
  _annotations: any;
  _activeAnnotation: any;

  constructor(plugin: Object) {
    super(plugin);
    this.initAPI(this, 'AnnotationState');
  }

  // Sets _annotations w/Annotation objects from input array
  set annotations(annotationsData) {
    const _this2 = this;
    this._annotations = annotationsData.map((a) => new Annotation(a, _this2.plugin));
  }

  get annotations() {
    return this._annotations;
  }

  set activeAnnotation(annotation) {
    this._activeAnnotation = annotation;
  }

  // Get current active annotation or
  get activeAnnotation() {
    return this._activeAnnotation || { close: () => {} };
  }

  // Serialize data
  get data() {
    return this._annotations.map((a) => a.data);
  }

  openAnnotation(id) {
    const annotation = this.findAnnotation(id);

    // Closing annot if clicked on when already active
    if (this.activeAnnotation && this.activeAnnotation.id == annotation.id) {
      this.closeAnnotation();
      return;
    }
    // Close if clicking on new object after already clicking on another
    else if (this.activeAnnotation) {
      this.closeAnnotation();
    }

    this.activeAnnotation = annotation;
    const eventData = {
      annotation: annotation.data,
      itemId: this.plugin.getItem().id,
      instanceId: this.plugin.instanceId,
    };

    this.plugin.fire('annotationClickedIn', eventData);

    this.plugin.fire('highlightAnnotation', eventData);
  }

  closeAnnotation() {
    if (!this.activeAnnotation) return;
    this.activeAnnotation = null;

    const eventData = {
      annotation: this.activeAnnotation.data,
      itemId: this.plugin.getItem().id,
      instanceId: this.plugin.instanceId,
    };

    this.plugin.fire('annotationClickedOut', eventData);
  }

  destroyAnnotationById(id) {
    const annotation = this.findAnnotation(id);
    if (annotation) annotation.teardown();
  }

  removeAnnotation(annotation: Annotation) {
    const { id } = annotation;

    const i = this._annotations.indexOf(annotation);
    this._annotations.splice(i, 1);
    this.stateChanged();
    this.plugin.fire('annotationDeleted', {
      id: id,
      allAnnotations: this.data,
      itemId: this.plugin.getItem().id,
      instanceId: this.plugin.instanceId,
    });
  }

  editAnnotation(annotation: Annotation) {
    const annotIndex = this.annotations.indexOf(annotation);
    if (annotation) {
      this.plugin.controls.editingStart(this.annotations[annotIndex]);
    }
  }

  saveEditedAnnotation(annotationId, isWSCall = false) {
    if (this.plugin.controls.uiState.editing) {
      const annotation = this.findAnnotation(annotationId);
      if (annotation) annotation.updateAnnotation(null, isWSCall);
      this.plugin.controls.cancelAddNew();
    }
  }

  newAnnotationUpdate(annotationData) {
    const newAnnotation = new Annotation(annotationData, this.plugin);
    this._annotations.push(newAnnotation);
    if (!this.plugin.controls.uiState.adding && !this.plugin.controls.uiState.editing) {
      this.plugin.controls.cancelAddNew();
    }
  }

  updateAnnotationById(annotation) {
    const annotationIndex = this.annotations.findIndex((obj) => obj.id === annotation.id);
    this.annotations[annotationIndex].updateAnnotation(annotation);
    if (!this.plugin.controls.uiState.adding && !this.plugin.controls.uiState.editing) {
      this.plugin.controls.cancelAddNew();
    }
  }

  findComment(id) {
    var _ref;

    function _toConsumableArray(arr) {
      if (Array.isArray(arr)) {
        for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) {
          arr2[i] = arr[i];
        }
        return arr2;
      } else {
        return Array.from(arr);
      }
    }

    let comments = this.annotations.map(function (a) {
      return a.commentList.comments;
    });
    comments = (_ref = []).concat.apply(_ref, _toConsumableArray(comments)); // flatten 2d array
    return comments.find(function (c) {
      return c.id == id;
    });
  }

  // Returns annotation object given ID
  findAnnotation(id) {
    return this.annotations.find((a) => a.id === id);
  }

  createAndAddAnnotation(data) {
    this.plugin.controls.uiState.adding && this.plugin.controls.cancelAddNew();

    const annotation = Annotation.newFromData(
      data.commentStr || '',
      data.parentId || null,
      this.plugin,
      data.id,
      data.range,
      data.privateConditionKey
    );
    this.addNewAnnotation(annotation);
  }

  // Add a new annotation
  addNewAnnotation(annotation) {
    this._annotations.push(annotation);
    this.stateChanged();
    const data = {
      newAnnotation: annotation,
      allAnnotations: this.data,
      itemId: this.plugin.getItem().id,
      instanceId: this.plugin.instanceId,
    };
    this.plugin.fire('annotationCreated', data);
  }

  stateChanged() {
    this.plugin.fire('onStateChanged', this.data);
  }

  // Reset internal state properties
  resetData() {
    this.annotations = [];
  }

  teardown() {
    this.annotations.forEach((annotation) => {
      annotation.teardown(false);
    });
    this.resetData();
    super.teardown();
    return null;
  }
}
